import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, UtensilsCrossed, Cpu, Handshake, Loader2 } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRIES = [
  {
    value: "nok_chhonk_b2b",
    label: "Nok Chhonk B2B Supply",
    desc: "Hotels, PGs & offices — mess and tiffin contracts",
    icon: UtensilsCrossed,
    tone: "amber",
  },
  {
    value: "mozekaql_it",
    label: "Mozekaql IT Solutions",
    desc: "Software, cloud & analytics engagements",
    icon: Cpu,
    tone: "cyan",
  },
  {
    value: "general_partnership",
    label: "General Partnership",
    desc: "Collaborations, distribution & foundation work",
    icon: Handshake,
    tone: "white",
  },
];

export default function Contact() {
  const [params] = useSearchParams();
  const [inquiry, setInquiry] = useState("general_partnership");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = "Contact — TOSIASI CREATIVES PRIVATE LIMITED";
  }, []);

  useEffect(() => {
    const q = params.get("inquiry");
    if (q === "nok-chhonk-b2b") setInquiry("nok_chhonk_b2b");
    if (q === "mozekaql-it") setInquiry("mozekaql_it");
    if (q === "general") setInquiry("general_partnership");
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, { ...form, inquiry_type: inquiry });
      if (data.status === "success") {
        toast.success("Enquiry received", {
          description: "It has landed directly with the promoters. We'll reply to your email shortly.",
        });
        setForm({ name: "", email: "", phone: "", message: "" });
      }
    } catch (err) {
      toast.error("Couldn't send your enquiry", {
        description: err?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <section className="relative pt-40 pb-24 md:pt-48">
        <div className="glow-amber pointer-events-none absolute -left-40 top-0 h-[480px] w-[480px] opacity-70" />
        <div className="glow-cyan pointer-events-none absolute -right-40 top-40 h-[520px] w-[520px] opacity-70" />
        <div className="relative mx-auto max-w-7xl px-6">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="eyebrow"
          >
            Contact
          </motion.p>
          <h1 className="mt-8 max-w-3xl font-display text-[clamp(2.4rem,6vw,4.6rem)] font-semibold leading-[1.05] tracking-tight text-white">
            {["Tell us where", "to plug in."].map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={i === 1 ? "block font-light text-slate-400" : "block"}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-16 grid gap-10 lg:grid-cols-12">
            {/* INFO */}
            <div className="space-y-6 lg:col-span-5">
              <Reveal>
                <div className="glass rounded-3xl p-8" data-testid="contact-info-card">
                  <a
                    href="mailto:tosiasi@tosiasicreatives.com"
                    data-testid="contact-email-link"
                    className="flex items-center gap-4 text-slate-200 transition-colors hover:text-amber-400"
                  >
                    <span className="glass flex h-11 w-11 items-center justify-center rounded-xl">
                      <Mail size={17} className="text-amber-400" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</span>
                      <span className="mt-1 block text-sm">tosiasi@tosiasicreatives.com</span>
                    </span>
                  </a>
                  <div className="mt-7 flex items-start gap-4 text-slate-200">
                    <span className="glass flex h-11 w-11 shrink-0 items-center justify-center rounded-xl">
                      <MapPin size={17} className="text-cyan-400" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        Registered office
                      </span>
                      <span className="mt-1 block text-sm">Vaishali Nagar, Jaipur, Rajasthan, India</span>
                    </span>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="glass rounded-3xl p-8">
                  <p className="eyebrow">What happens next</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-400">
                    Every enquiry lands directly with the promoters — not a ticketing queue. We read all of them and
                    reply from <span className="text-slate-200">tosiasi@tosiasicreatives.com</span>.
                  </p>
                </div>
              </Reveal>
            </div>

            {/* FORM */}
            <Reveal delay={0.05} className="lg:col-span-7">
              <form
                data-inquiry={inquiry}
                onSubmit={submit}
                data-testid="contact-form"
                className="glass rounded-3xl p-8 md:p-10"
              >
                <p className="eyebrow">Inquiry type</p>
                <div className="mt-5 grid gap-3 md:grid-cols-3">
                  {INQUIRIES.map((q) => {
                    const active = inquiry === q.value;
                    return (
                      <button
                        type="button"
                        key={q.value}
                        data-testid={`inquiry-card-${q.value.replace(/_/g, "-")}`}
                        onClick={() => setInquiry(q.value)}
                        aria-pressed={active}
                        className={`rounded-2xl border p-5 text-left transition-all duration-300 ${
                          active
                            ? q.tone === "amber"
                              ? "border-amber-400/60 bg-amber-400/[0.08] shadow-[0_0_28px_rgba(245,158,11,0.15)]"
                              : q.tone === "cyan"
                              ? "border-cyan-400/60 bg-cyan-400/[0.08] shadow-[0_0_28px_rgba(6,182,212,0.15)]"
                              : "border-white/40 bg-white/[0.07]"
                            : "border-white/10 bg-white/[0.03] hover:border-white/25"
                        }`}
                      >
                        <q.icon
                          size={18}
                          className={active ? (q.tone === "cyan" ? "text-cyan-300" : q.tone === "amber" ? "text-amber-400" : "text-white") : "text-slate-500"}
                        />
                        <p className="mt-3 text-sm font-semibold text-white">{q.label}</p>
                        <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{q.desc}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-8 grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Name *
                    </label>
                    <input
                      id="cf-name"
                      data-testid="contact-name-input"
                      className="field"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={set("name")}
                      required
                      minLength={2}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Email *
                    </label>
                    <input
                      id="cf-email"
                      data-testid="contact-email-input"
                      type="email"
                      className="field"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={set("email")}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="cf-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Phone *
                    </label>
                    <input
                      id="cf-phone"
                      data-testid="contact-phone-input"
                      type="tel"
                      className="field"
                      placeholder="+91 98XXX XXXXX"
                      value={form.phone}
                      onChange={set("phone")}
                      required
                      minLength={7}
                      maxLength={20}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Message <span className="text-slate-600">(optional)</span>
                    </label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-message-input"
                      className="field min-h-28 resize-y"
                      placeholder="Tell us about your kitchens, your team size, or the problem you want solved."
                      value={form.message}
                      onChange={set("message")}
                      maxLength={2000}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="btn btn-amber mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send enquiry <Send size={15} />
                    </>
                  )}
                </button>
                <p className="mt-4 text-[11px] text-slate-500">
                  By sending, you agree to be contacted about your enquiry. No newsletters, no spam.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
